// @flow
import React from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import type { Styles } from 'shared/types';
import { S, DARK } from '~/app/ui/tokens';

type OptionsProps = {
  maxLength?: number,
  rows?: number,
  fullWidth?: boolean
}

type Props = {
  classes?: Object,
  className?: string,
  placeholder?: string,
  value: string,
  onChange?: (event: any) => void,
  disabled?: boolean,
  options?: OptionsProps,
  /** The field's name for screen readers and AI assistants, when no <label> points at it. */
  ariaLabel?: string,
};

export default function TextArea(props: Props) {
  const {
    placeholder,
    value,
    onChange,
    disabled,
    options,
    className,
    ariaLabel
  } = props;

  const c: Styles<typeof styles> = useStyles(props);

  const characterLimit = options?.maxLength;
  const characterCount = value?.length;
  const rows = options?.rows;
  const fullWidth = options?.fullWidth;

  return (
    <>
      <textarea
        aria-label={ariaLabel}
        className={cx(c.textarea, className)}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={!!disabled}
        maxLength={characterLimit}
        rows={rows}
        style={fullWidth ? { width: '100%', marginLeft: 0 } : undefined}
      />
      {characterLimit >= 0 && (
        <p className={cx(
          c.characterLimitMessage,
          characterCount >= characterLimit ? c.errorColor : c.defaultColor
        )}>
        {characterCount}/{characterLimit}
        </p>
      )}
    </>
  );
}

const styles = {
  characterLimitMessage: {
    fontSize: '12px',
    marginLeft: '24px',
    paddingLeft: '3px',
    marginTop: '4px',
    textAlign: 'right',
    width: '90%'
  },
  defaultColor: {
    color: 'gray',
    [DARK]: { color: S.muted }
  },
  errorColor: {
    color: '#993333',
    [DARK]: { color: S.red }
  },
  textarea: {
    border: '1px solid hsl(0, 0%, 90%)',
    padding: '10px 10px',
    borderRadius: '3px',
    fontSize: '13px',
    color: 'hsl(0, 0%, 20%)',
    fontWeight: '400',
    marginLeft: '8px',
    maxWidth: '100%',
    width: '90%',
    outline: 'none',
    '&:disabled': {
      backgroundColor: 'hsl(0, 0%, 60%)',
      cursor: 'not-allowed'
    },
    [DARK]: {
      background: S.field,
      borderColor: S.fieldLine,
      color: S.fg,
      '&:disabled': { background: S.chromeHover }
    }
  }
};

const useStyles = createUseStyles(styles);
